import { Button, Modal, NumberInput, Select, Stack, Text, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { PortalPartnerTypeEnum } from '@routellers/types/dist/partners-portal/enum/portal-partner-type.enum';
import { ImageTypeEnum } from '@routellers/types/dist/upload/enum/image-type.enum';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api, apiErrorMessage } from '../api';
import { PhotoUpload } from '../components/editor';
import { enumOptions } from '../i18n';

import { CitySearchSelect } from './city-search-select';

type Props = {
    opened: boolean;
    onClose: () => void;
    /** When omitted, dialog lets admin pick author vs venue. */
    type?: PortalPartnerTypeEnum;
    onCreated: (id: number) => void;
};

export const PartnerCreateDialog = ({ opened, onClose, type: fixedType, onCreated }: Props) => {
    const { t } = useTranslation();
    const typeOptions = useMemo(
        () => enumOptions(Object.values(PortalPartnerTypeEnum), t, 'enums.portalPartnerType'),
        [t],
    );
    const [type, setType] = useState<PortalPartnerTypeEnum>(
        fixedType || PortalPartnerTypeEnum.AUTHOR,
    );
    const isVenue = type === PortalPartnerTypeEnum.VENUE;
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
    const [companyName, setCompanyName] = useState('');
    const [cityId, setCityId] = useState<number | null>(null);
    const [commissionPercent, setCommissionPercent] = useState<number | ''>(0);
    const [saving, setSaving] = useState(false);
    const [tempPassword, setTempPassword] = useState<string | null>(null);
    const isAuthor = type === PortalPartnerTypeEnum.AUTHOR;

    useEffect(() => {
        if (!opened) return;
        setType(fixedType || PortalPartnerTypeEnum.AUTHOR);
        setName('');
        setEmail('');
        setPassword('');
        setAvatarUrl(null);
        setCompanyName('');
        setCityId(null);
        setCommissionPercent(0);
        setTempPassword(null);
    }, [opened, fixedType]);

    const create = async () => {
        if (!name.trim() || !email.trim()) {
            notifications.show({
                color: 'red',
                message: t('editors.partnerCreate.nameEmailRequired'),
            });

            return;
        }

        if (isVenue && (!companyName.trim() || cityId == null)) {
            notifications.show({
                color: 'red',
                message: t('editors.partnerCreate.venueRequiresFields'),
            });

            return;
        }

        setSaving(true);

        try {
            const { data } = await api.partnersPortalAdminApi.createPartner({
                data: {
                    type,
                    name: name.trim(),
                    email: email.trim(),
                    password: password.trim() || undefined,
                    avatarUrl: isAuthor ? avatarUrl : undefined,
                    companyName: isVenue ? companyName.trim() : undefined,
                    cityId: isVenue ? cityId! : undefined,
                    commissionPercent: commissionPercent === '' ? 0 : Number(commissionPercent),
                },
            });

            if (data?.temporaryPassword) {
                setTempPassword(data.temporaryPassword);
                notifications.show({
                    color: 'green',
                    title: t('editors.partnerCreate.temporaryPasswordTitle'),
                    message: t('editors.partnerCreate.temporaryPasswordMessage', {
                        password: data.temporaryPassword,
                    }),
                    autoClose: false,
                });
            } else {
                notifications.show({ color: 'green', message: t('editors.partnerCreate.created') });
            }

            if (data?.id) {
                onCreated(data.id);
                if (!data.temporaryPassword) onClose();
            }
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={
                fixedType
                    ? isVenue
                        ? t('editors.partnerCreate.newVenue')
                        : t('editors.partnerCreate.newAuthor')
                    : t('editors.partnerCreate.newPartner')
            }
            centered
        >
            <Stack gap='md'>
                {!fixedType && (
                    <Select
                        label={t('editors.partnerCreate.type')}
                        data={typeOptions}
                        value={type}
                        onChange={(v) =>
                            setType((v as PortalPartnerTypeEnum) || PortalPartnerTypeEnum.AUTHOR)
                        }
                    />
                )}
                <TextInput
                    label={t('editors.partnerCreate.contactName')}
                    required
                    value={name}
                    onChange={(e) => setName(e.currentTarget.value)}
                />
                {isAuthor && (
                    <PhotoUpload
                        variant='avatar'
                        label={t('editors.partner.avatar')}
                        description={t('editors.partner.avatarHint')}
                        value={avatarUrl}
                        onChange={setAvatarUrl}
                        imageType={ImageTypeEnum.AVATAR}
                    />
                )}
                <TextInput
                    label={t('editors.partnerCreate.email')}
                    required
                    type='email'
                    value={email}
                    onChange={(e) => setEmail(e.currentTarget.value)}
                />
                <TextInput
                    label={t('editors.partnerCreate.passwordOptional')}
                    description={t('editors.partnerCreate.passwordHint')}
                    type='password'
                    value={password}
                    onChange={(e) => setPassword(e.currentTarget.value)}
                />
                {isVenue && (
                    <>
                        <TextInput
                            label={t('editors.partnerCreate.companyName')}
                            required
                            value={companyName}
                            onChange={(e) => setCompanyName(e.currentTarget.value)}
                        />
                        <CitySearchSelect value={cityId} onChange={setCityId} />
                    </>
                )}
                <NumberInput
                    label={t('editors.partnerCreate.commissionPercent')}
                    min={0}
                    max={100}
                    value={commissionPercent}
                    onChange={(v) => setCommissionPercent(typeof v === 'number' ? v : '')}
                />
                {tempPassword ? (
                    <>
                        <Text size='sm' c='orange'>
                            {t('editors.partnerCreate.tempPasswordNotice', {
                                password: tempPassword,
                            })}
                        </Text>
                        <Button onClick={onClose}>{t('editors.partnerCreate.close')}</Button>
                    </>
                ) : (
                    <Button loading={saving} onClick={create}>
                        {t('editors.partnerCreate.create')}
                    </Button>
                )}
            </Stack>
        </Modal>
    );
};
